import { MenuFoldOutlined, MenuUnfoldOutlined } from '@ant-design/icons';
import { Button, Layout, theme } from 'antd';
import React, { type CSSProperties, type ReactNode, useState } from 'react';

import { SPACING } from '../theme/tokens';

type SiderContentProps = {
  siderChildren?: ReactNode;
  contentChildren: ReactNode;
};

const height = 'calc(100vh - 8.5rem)';

const styleLayoutContent: CSSProperties = {
  minHeight: height,
  flexDirection: 'row',
};

const styleSider: CSSProperties = {
  height,
  overflow: 'auto',
  position: 'sticky',
  top: 0,
  alignSelf: 'flex-start',
};

const SIDER_WIDTH = '15rem';
const SIDER_COLLAPSED_WIDTH = '2.5rem';

function SiderContentComponent({
  siderChildren = undefined,
  contentChildren,
}: SiderContentProps): React.ReactElement {
  const { token } = theme.useToken();
  const [collapsed, setCollapsed] = useState(false);

  const hasSider = Boolean(siderChildren);

  const contentWidth = !hasSider
    ? '100vw'
    : `calc(100vw - ${collapsed ? SIDER_COLLAPSED_WIDTH : SIDER_WIDTH})`;

  const styleLayout: CSSProperties = {
    minHeight: height,
    maxWidth: contentWidth,
    width: contentWidth,
    minWidth: contentWidth,
  };

  const styleToggleButton: CSSProperties = {
    display: 'flex',
    justifyContent: collapsed ? 'center' : 'flex-end',
    padding: SPACING.xs,
  };

  return (
    <Layout style={styleLayoutContent}>
      {hasSider && (
        <Layout.Sider
          style={{ ...styleSider, backgroundColor: token.colorBgContainer }}
          width={SIDER_WIDTH}
          collapsed={collapsed}
          collapsedWidth={SIDER_COLLAPSED_WIDTH}
          collapsible
          trigger={null}
          breakpoint="md"
          onBreakpoint={(broken: boolean): void => {
            setCollapsed(broken);
          }}
        >
          <div style={styleToggleButton}>
            <Button
              type="text"
              size="large"
              icon={collapsed ? <MenuUnfoldOutlined /> : <MenuFoldOutlined />}
              onClick={(): void => setCollapsed(!collapsed)}
              aria-label={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
              style={{ color: token.colorText }}
            />
          </div>
          {!collapsed && siderChildren}
        </Layout.Sider>
      )}
      <Layout style={styleLayout}>
        <Layout.Content
          style={{
            backgroundColor: token.colorBgLayout,
            padding: SPACING.sm,
            minHeight: height,
          }}
        >
          {contentChildren}
        </Layout.Content>
      </Layout>
    </Layout>
  );
}

export default SiderContentComponent;
